const STORAGE_KEY = 'scoreboardData';
const PREPARED_SIG_KEY = 'scoreboardPreparedSig';
const LEADERBOARD_NAVIGATED_SIG_KEY = 'pvpLeaderboardNavigatedSig';

export function readScoreboardMatchSig() {
  const data = readScoreboardData();
  return String(data?.endedMatchSig ?? '').trim();
}

export function isScoreboardGamePrepared(matchSig) {
  const sig = String(matchSig ?? '').trim();
  if (!sig) return false;
  try {
    return sessionStorage.getItem(PREPARED_SIG_KEY) === sig;
  } catch {
    return false;
  }
}

export function markScoreboardGamePrepared(matchSig) {
  const sig = String(matchSig ?? '').trim() || '1';
  try {
    sessionStorage.setItem(PREPARED_SIG_KEY, sig);
  } catch {
    /* ignore */
  }
}

export function appendScoreboardEndedMatchSig(matchSig) {
  const data = readScoreboardData();
  if (!data) return;
  writeScoreboardData({ ...data, endedMatchSig: String(matchSig ?? '').trim() });
}

export function writeScoreboardData(payload) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

export function readScoreboardData() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.players)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearScoreboardData() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function clearScoreboardSession() {
  clearScoreboardData();
  try {
    sessionStorage.removeItem(PREPARED_SIG_KEY);
    sessionStorage.removeItem(LEADERBOARD_NAVIGATED_SIG_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Mark that we've navigated to leaderboard for a specific match signature.
 * This prevents duplicate navigations during state transitions.
 */
export function markLeaderboardNavigatedForMatch(matchSig) {
  const sig = String(matchSig ?? '').trim();
  if (!sig) return;
  try {
    sessionStorage.setItem(LEADERBOARD_NAVIGATED_SIG_KEY, sig);
  } catch {
    /* ignore */
  }
}

/**
 * Check if we've already navigated to leaderboard for this match.
 */
export function hasNavigatedToLeaderboardForMatch(matchSig) {
  const sig = String(matchSig ?? '').trim();
  if (!sig) return false;
  try {
    return sessionStorage.getItem(LEADERBOARD_NAVIGATED_SIG_KEY) === sig;
  } catch {
    return false;
  }
}

/**
 * Clear leaderboard navigation flag - call on explicit Play Again.
 */
export function clearLeaderboardNavigatedFlag() {
  try {
    sessionStorage.removeItem(LEADERBOARD_NAVIGATED_SIG_KEY);
  } catch {
    /* ignore */
  }
}

function stripNullWords(value) {
  return String(value ?? '')
    .replace(/\bnull\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Resolve a human-readable label from name, first/last name, email, or employee id. */
export function resolvePlayerDisplayName(nameOrRecord, emailMaybe) {
  const record =
    nameOrRecord != null &&
    typeof nameOrRecord === 'object' &&
    !Array.isArray(nameOrRecord)
      ? nameOrRecord
      : { name: nameOrRecord, email: emailMaybe };

  const fromName = stripNullWords(record.name);
  if (fromName && fromName.toLowerCase() !== 'null null') return fromName;

  const first = stripNullWords(record.firstName ?? record.first_name);
  const last = stripNullWords(record.lastName ?? record.last_name);
  const fullName = `${first} ${last}`.trim();
  if (fullName) return fullName;

  const email = String(record.email ?? '').trim();
  if (email) return email;

  const empId = String(record.emp_id ?? record.employeeId ?? '').trim();
  if (empId) return empId;

  return 'Player';
}

export function playersFromUpdateScoreResult(scoreUsers, updateResult) {
  const results = Array.isArray(updateResult?.results) ? updateResult.results : [];
  const resultByUserId = {};
  for (const row of results) {
    const uid = String(row?.userId ?? '').trim();
    if (!uid) continue;
    resultByUserId[uid] = row;
  }
  return scoreUsers.map((u) => {
    const uid = String(u?.userId ?? '').trim();
    const row = resultByUserId[uid];
    const fallback = Number(u?.score);
    const merged = {
      ...u,
      name: row?.name ?? u?.name,
      email: row?.email ?? u?.email,
    };
    return {
      userId: uid,
      name: resolvePlayerDisplayName(merged),
      email: String(merged.email ?? '').trim(),
      score:
        Number.isFinite(Number(row?.points))
          ? Number(row.points)
          : Number.isFinite(fallback)
            ? fallback
            : 0,
    };
  });
}
